import { defineStore } from 'pinia'
import TextCell from '@/onenetwork/components/datatable/TextCell.vue'
import DateTimeCell from '@/onenetwork/components/datatable/DateTimeCell.vue'
import TagCell from '@/onenetwork/components/datatable/TagCell.vue'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import AddressCell from '@/onenetwork/components/datatable/AddressCell.vue'
import ListingSubjectCell from '@/onenetwork/components/datatable/ListingSubjectCell.vue'
import ListingStatisticCell from '@/onenetwork/components/datatable/ListingStatisticCell.vue'
import AccountCell from '@/onenetwork/components/datatable/AccountCell.vue'
import RealestateAPI from '@/modules/realestate/constants/realestate.api.uri.const.ts'
import { buildFilterObject } from '@/utils/filter'
import { formatTableDataListing } from '../helper/table'
import { formatDataToSubmit } from '../helper/listing'
import { LISTING_TYPE_OPTIONS, LISTING_PROCESSING_STATUS } from '../constants/realestate.const'
import { markRaw } from 'vue'

export interface tabItem {
  id: string
  label: string
  active: boolean
}

export enum ListingListActionTypes {
  FILTERING = 'FILTERING',
  PENDING = 'PENDING',
  FETCHING = 'FETCHING',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_FAILURE = 'FETCH_FAILURE',
  CREATING = 'CREATING',
  CREATE_SUCCESS = 'CREATE_SUCCESS',
  CREATE_FAILURE = 'CREATE_FAILURE',
  DELETING = 'DELETING',
  DELETE_SUCCESS = 'DELETE_SUCCESS',
  DELETE_FAILURE = 'DELETE_FAILURE',
}

interface ListingListStore {
  action_type: ListingListActionTypes
  action_mess: string
  uuid: string
  data: Object
  filter: Array<{
    key: string
    type: string
    label: string
    placeholder: string
    value: any
    options?: Array<{ label: string; value: string }>
  }>
  pageSize: number
  page: number
  tabs: Array<any>
  tableConfig: any
  selectedIds: Array<string | number>
}

export const useListingListStore = defineStore('listing.list', {
  state: (): ListingListStore => ({
    action_type: ListingListActionTypes.PENDING,
    action_mess: '',
    uuid: '',
    data: {
      list: [],
      totalRecords: 0,
      totalPage: 0,
    },
    filter: [
      {
        key: 'province_id',
        type: 'region-level-1',
        label: 'Lọc theo tỉnh/thành',
        placeholder: 'Chọn tỉnh/thành',
        value: '',
        options: null,
      },
      {
        key: 'ward_id',
        type: 'region-level-2',
        label: 'Lọc theo phường / xã',
        placeholder: 'Chọn phường / xã',
        value: '',
        options: null,
      },
      {
        key: 'map_sheet_number',
        type: 'text',
        label: 'Số tờ',
        placeholder: 'Nhập số tờ',
        value: '',
      },
      {
        key: 'plot_number',
        type: 'text',
        label: 'Số thửa',
        placeholder: 'Nhập số thửa',
        value: '',
      },
      {
        key: 'keyword',
        type: 'text',
        label: 'Lọc theo tiêu đề',
        placeholder: 'Nhập tiêu đề',
        value: '',
      },
      {
        key: 'address',
        type: 'text',
        label: 'Lọc theo địa chỉ',
        placeholder: 'Nhập địa chỉ',
        value: '',
      },
      // {
      //   key: 'status',
      //   type: 'select-multiple',
      //   label: 'Lọc theo trạng thái',
      //   placeholder: 'Chọn trạng thái',
      //   value: '',
      //   options: [
      //     { label: 'Tất cả', value: '' },
      //     { label: 'Hoạt động', value: 'active' },
      //     { label: 'Đang KYC', value: 'pending' },
      //     { label: 'Đang tạm ngưng', value: 'inactive' },
      //     { label: 'Đã xóa', value: 'trash' },
      //   ],
      // },
      {
        key: 'listing_type',
        type: 'select-multiple',
        label: 'Lọc theo loại tin',
        placeholder: 'Chọn phân loại',
        value: '',
        options: LISTING_TYPE_OPTIONS,
      },
      {
        key: 'is_flash_offer',
        type: 'select-multiple',
        label: 'Flash offer',
        placeholder: 'Chọn flash offer',
        value: '',
        options: [
          { label: 'Có', value: 'true' },
          { label: 'Không', value: 'false' },
        ],
      },
    ],
    pageSize: 10,
    page: 1,
    tabs: [
      {
        id: 'list',
        label: 'Danh sách tin đăng BDS',
        active: true,
      },
    ],
    tableConfig: {
      canShowHide: true,
      columns: {
        code: {
          label: 'Mã tin',
          component: markRaw(TextCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        type: {
          label: 'Loại tin',
          component: markRaw(TagCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        address: {
          label: 'Địa chỉ',
          component: markRaw(AddressCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        subject: {
          label: 'Thông tin',
          component: markRaw(ListingSubjectCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        author: {
          label: 'Đăng bởi',
          component: markRaw(AccountCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        statistic: {
          label: 'Thống kê',
          component: markRaw(ListingStatisticCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        status: {
          label: 'Trạng thái',
          component: markRaw(TagCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        regdate: {
          label: 'Ngày đăng ký',
          component: markRaw(DateTimeCell),
          class: '',
          visible: true,
          sort: '',
          config: {},
        },
      },
    },
    selectedIds: [],
  }),
  getters: {
    sortFilter: (state) => {
      const sort: any = []
      if (Object.keys(state.tableConfig?.columns).length > 0) {
        Object.keys(state.tableConfig?.columns).forEach((col) => {
          if (state.tableConfig.columns[col].sort) {
            sort.push([col, state.tableConfig.columns[col].sort])
          }
        })
      }
      return sort
    },
  },
  actions: {
    //TAB ACTION
    changeTab(targetId: string) {
      for (const tab of this.tabs) {
        tab.active = tab.id === targetId
      }
    },
    closeTab(targetId: string) {
      const index = this.tabs.findIndex((tab) => tab.id === targetId)
      if (index !== -1) {
        this.tabs.splice(index, 1)
      }
    },
    closeAllTab() {
      this.tabs = [
        {
          id: 'list',
          label: 'Danh sách tin đăng BDS',
          active: true,
        },
      ]
    },
    newTab(item: tabItem) {
      const exists = this.tabs.some((tab) => tab.id === item.id)
      if (!exists) {
        this.tabs.push(item)
      }
      this.changeTab(item.id)
    },

    //FILTER ACTION
    setFilter(filter: any) {
      this.filter = filter
      this.action_type = ListingListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changeFilter(key: string, value: any) {
      const filterItem = this.filter.find((item: { key: string }) => item.key === key)
      if (filterItem) {
        filterItem.value = value
        this.action_type = ListingListActionTypes.FILTERING
        this.uuid = uuid.v4()
      }
    },
    putFilter(key: string, value: any) {
      this.filter = this.filter.map((field) => {
        if (field.key === key) {
          return { ...field, value: value }
        }
        return field
      })
    },

    //PAGE ACTION
    changePage(page: number) {
      this.page = page
      this.action_type = ListingListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changePageSize(size: number) {
      this.pageSize = size
      this.action_type = ListingListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    //TABLE CONFIG ACTION
    toggleColumnVisibility(columnCode: string, visible: boolean) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].visible = visible
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },
    toggleSort(columnCode: string, sort: string) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].sort = sort
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },

    //DATA ACTION
    async fetchList() {
      return new Promise(async (resolve, reject) => {
        try {
          this.action_type = ListingListActionTypes.FETCHING
          const sort = this.sortFilter
          const pagination = {
            page: this.page,
            pageSize: this.pageSize,
            sort,
            processing_status: [
              LISTING_PROCESSING_STATUS.APPROVED,
              LISTING_PROCESSING_STATUS.REJECTED,
              LISTING_PROCESSING_STATUS.COMPLETED,
              LISTING_PROCESSING_STATUS.EXPIRED,
            ],
            order_by: 'created_at',
            order_dir: 'desc',
          }
          const res = await APIClient.get(RealestateAPI.listing.list, {
            params: buildFilterObject(this.filter, pagination),
          })
          const { data } = res.data
          this.data = {
            list: formatTableDataListing(data.data || []),
            totalRecords: data.total ?? 0,
            totalPage: data.last_page ?? 0,
          }
          this.action_type = ListingListActionTypes.FETCH_SUCCESS
          resolve(res)
        } catch (e) {
          this.action_type = ListingListActionTypes.FETCH_FAILURE
          reject(e)
        }
      })
    },
    async createListing(listing: any) {
      return new Promise(async (resolve, reject) => {
        try {
          this.action_type = ListingListActionTypes.CREATING
          const data = formatDataToSubmit(listing)
          const res = await APIClient.post(RealestateAPI.listing.create, data)
          this.action_type = ListingListActionTypes.CREATE_SUCCESS
          resolve(res.data.data)
        } catch (e) {
          console.error('Error creating listing:', e)
          this.action_type = ListingListActionTypes.CREATE_FAILURE
          reject(e?.response?.data)
        }
      })
    },
    async deleteListing(listing_ids: string) {
      return new Promise(async (resolve, reject) => {
        try {
          this.action_type = ListingListActionTypes.DELETING
          const res = await APIClient.delete(RealestateAPI.listing.delete, {
            data: { listing_ids },
          })
          this.action_type = ListingListActionTypes.DELETE_SUCCESS
          resolve(true)
        } catch (e) {
          console.error('Error deleting listing:', e)
          this.action_type = ListingListActionTypes.DELETE_FAILURE
          reject(e?.response?.data)
        }
      })
    },
  },
})
